import { create } from 'zustand';
import type { FilterGroup } from '../../core/types/workspaceTypes';
import {
  applySampleToAggregate,
  keyId,
  MAX_SAMPLES_PER_COLUMN,
  type ColumnAggregate,
  type ColumnKey,
} from './jsonStructureAggregatorStore';

/**
 * Ephemeral, session-only aggregates for JSON samples collected while the
 * Table Explorer grid has active filters. One bucket per column; a bucket
 * carries the filter signature it was collected under and is REPLACED when
 * samples arrive under a different signature (that's the filter-change
 * reset). NEVER flushed to MetaInfo — filtered samples must not skew the
 * persisted whole-column structure cache (this module must not import
 * metaInfoApi). See Docs/JsonDiscriminatedStructure.md (Path A).
 */
export type FilteredColumnAggregate = ColumnAggregate & { signature: string };

/**
 * Canonical session signature for a filter set. Producer (TableExplorerTab →
 * grid) and consumer (peek popover) both derive it from the same ordered
 * arrays ([...locked, ...user]), so plain JSON.stringify is stable for
 * same-session equality. Never persisted — cross-session canonicalization
 * concerns (A AND B vs B AND A) intentionally out of scope.
 */
export function filterSignature(filters: ReadonlyArray<FilterGroup>): string {
  return JSON.stringify(filters);
}

type State = { filteredAggregates: Record<string, FilteredColumnAggregate> };
type Actions = {
  pushFilteredSample: (key: ColumnKey, signature: string, value: unknown) => void;
  reset: () => void;
};

export const useJsonStructureFilteredAggregatorStore = create<State & Actions>((set, get) => ({
  filteredAggregates: {},

  pushFilteredSample: (key, signature, value) => {
    const id = keyId(key);
    const existing = get().filteredAggregates[id];
    const agg: FilteredColumnAggregate =
      existing && existing.signature === signature
        ? existing
        : {
            key,
            signature,
            pathTotal: 0,
            walkedSampleCount: 0,
            pathSeen: new Map(),
            pathTypes: new Map(),
            dirty: false,
          };
    if (agg.pathTotal >= MAX_SAMPLES_PER_COLUMN) return;
    applySampleToAggregate(agg, value);
    set((s) => ({ filteredAggregates: { ...s.filteredAggregates, [id]: agg } }));
  },

  reset: () => set({ filteredAggregates: {} }),
}));
